<template>
  <v-dialog
    v-model="dialog"
    max-width="1000"
    :fullscreen="$vuetify.display.smAndDown"
  >
    <v-card>
      <v-card-title class="redColor d-flex text-white">
        <!--TITLE-->
        <span class="headline">
          {{ $t("car_locations.cars_list.title") }}
        </span>
        <v-spacer></v-spacer>
        <!--CLOSE-->
        <v-btn variant="outlined" icon color="white" @click="close">
          <v-icon> mdi-close </v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <!-- ===================== MOBILE CARD LIST (< 960px) ===================== -->
        <div class="d-md-none cars-list-mobile-list mt-2 px-2 pb-4">
          <v-progress-linear
            v-if="loading"
            indeterminate
            color="red"
            class="mb-2"
          />

          <div
            v-for="(item, index) in paginatedCars"
            :key="item._id"
            class="cars-list-mobile-card"
            :class="{
              'cars-list-mobile-card--selected': index === activeRow,
            }"
            @click="activeRow = activeRow === index ? null : index"
          >
            <div class="cars-list-mobile-card__main">
              <div class="cars-list-mobile-card__media">
                <button
                  type="button"
                  class="cars-list-mobile-card__image-btn"
                  @click.stop="showImages(item.photos)"
                >
                  <img
                    :src="
                      item.photos && item.photos.length && item.photos[0].thumb
                        ? `https://leko-tech.fra1.cdn.digitaloceanspaces.com/${item.photos[0].thumb}`
                        : `https://portal.lekotech.com/logo_small.png`
                    "
                    class="cars-list-mobile-card__img"
                  />
                </button>
                <button
                  type="button"
                  class="cars-list-mobile-card__qr-value-btn"
                  @click.stop="
                    openQrDialog({
                      id: item._id,
                      type: 'cars',
                      vehicle: item,
                      printed: item.printed,
                    })
                  "
                >
                  {{ item._id ? item._id.slice(-6) : "" }}
                </button>
              </div>

              <div class="cars-list-mobile-card__info">
                <div class="cars-list-mobile-card__info-top">
                  <v-chip
                    v-if="item.status === 1 || item.status === '1'"
                    color="dark-grey"
                    size="small"
                    variant="outlined"
                    class="mb-2"
                  >
                    {{ $t("car_locations.cars_list.inventory") }}
                  </v-chip>
                  <v-chip
                    v-else-if="item.status === 2 || item.status === '2'"
                    color="red"
                    size="small"
                    variant="flat"
                    class="text-white mb-2"
                  >
                    {{ $t("car_locations.cars_list.history") }}
                  </v-chip>

                  <div class="text-body-2 font-weight-bold text-primary mt-2">
                    {{ item.brand?.title }}
                  </div>
                  <div class="text-caption text-grey-darken-1 mt-1">
                    {{ item.model?.description }}
                  </div>
                  <div class="text-caption text-grey-darken-1 mt-1">
                    {{ item.version?.description }}
                  </div>
                  <div class="text-caption text-grey-darken-1 mt-1">
                    {{ formatMonthYear(item.version?.startingDate) }} -
                    {{ formatMonthYear(item.version?.endingDate) }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <v-alert
            v-if="!loading && paginatedCars.length < 1"
            type="info"
            variant="tonal"
            class="mt-2"
          >
            No data available.
          </v-alert>
        </div>
        <!-- ===================================================================== -->

        <div class="cars-list-table-host pb-2">
          <v-data-table-server
            v-model:options="pagination"
            :headers="headers"
            :items="cars"
            :items-length="total"
            :items-per-page="pagination.itemsPerPage"
            :items-per-page-options="[
              { value: 5, title: '5' },
              { value: 10, title: '10' },
              { value: 25, title: '25' },
              { value: 50, title: '50' },
            ]"
            items-per-page-text="Rows per page"
            item-value="_id"
            return-object
            :loading="loading"
            class="elevation-1 nowrap-headers"
            @update:options="updatePagination"
          >
            <template #progress>
              <v-progress-linear color="red" indeterminate />
            </template>
            <template #item="{ item }">
              <tr>
                <td class="ma-1 pt-1 pb-1">
                  <v-row
                    class="pointer ma-1 text-center justify-center d-block"
                    @click="
                      openQrDialog({
                        id: item._id,
                        type: 'cars',
                        vehicle: item,
                        printed: item.printed,
                      })
                    "
                  >
                    <qr-code :size="60" :text="item._id" />
                    <h4>
                      {{ item._id.slice(item._id.length - 6, item._id.length) }}
                    </h4>
                  </v-row>
                </td>
                <td>
                  <img
                    :src="
                      item.photos && item.photos.length
                        ? `https://leko-tech.fra1.cdn.digitaloceanspaces.com/${item.photos[0].thumb}`
                        : `https://portal.lekotech.com/logo_small.png`
                    "
                    class="ma-1 pointer"
                    height="75"
                    width="100"
                    @click="showImages(item.photos)"
                  />
                </td>
                <td>
                  <v-chip
                    v-if="item.status === 1 || item.status === '1'"
                    color="dark-grey"
                    size="small"
                    variant="outlined"
                  >
                    {{ $t("car_locations.cars_list.inventory") }}
                  </v-chip>
                  <v-chip
                    v-else-if="item.status === 2 || item.status === '2'"
                    color="red"
                    size="small"
                    variant="flat"
                    class="text-white"
                  >
                    {{ $t("car_locations.cars_list.history") }}
                  </v-chip>
                </td>
                <td>{{ item.brand.title }}</td>
                <td>{{ item.model.description }}</td>
                <td>{{ item.version.description }}</td>
                <td>
                  <span>
                    {{ formatMonthYear(item.version.startingDate) }} -
                    {{ formatMonthYear(item.version.endingDate) }}
                  </span>
                </td>
              </tr>
            </template>
          </v-data-table-server>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment";
import VueQrCode from "vue-qr-generator";

import getRequest from "../../api/getRequest";

export default {
  components: {
    "qr-code": VueQrCode,
  },
  filters: {
    date(date) {
      moment.locale(localStorage.getItem("language"));
      const valid = moment(date).isValid();
      if (valid) return moment(date).format("MM/YYYY");
      return "";
    },
  },
  data: () => ({
    headers: [
      { title: "ID", sortable: true, key: "id" },
      { title: "Photo", sortable: false, key: "" },
      { title: "Location", sortable: true, key: "status" },
      { title: "Brand", sortable: true, key: "brand.brandCode" },
      { title: "Model", sortable: true, key: "model.modelCode" },
      { title: "Version", sortable: true, key: "version.versionCode" },
      { title: "Year", sortable: false, key: "" },
    ],
    pagination: {
      itemsPerPage: 5,
      page: 1,
      groupBy: ["_id"],
      sortBy: { key: "_id", order: "desc" },
      sortDesc: [true],
    },
    dialog: false,
    location: null,
    cars: [],
    total: 0,
    loading: false,
    activeRow: null,
  }),
  computed: {
    server() {
      return this.$serverUri;
    },
    paginatedCars() {
      const page = this.pagination.page || 1;
      const perPage = this.pagination.itemsPerPage || 5;
      const start = (page - 1) * perPage;
      return this.cars.slice(start, start + perPage);
    },
  },
  watch: {
    dialog(value) {
      if (!value) this.close();
    },
    pagination: {
      handler() {
        this.getCars();
      },
    },
  },
  created() {
    this.getLocale();
  },
  methods: {
    getLocale() {
      this.headers[0].title = this.$t("vehicle_entry.datatable.id");
      this.headers[1].title = this.$t("vehicle_entry.datatable.images");
      this.headers[2].title = this.$t("car_locations.cars_list.location");
      this.headers[3].title = this.$t("vehicle_entry.datatable.brand");
      this.headers[4].title = this.$t("vehicle_entry.datatable.model");
      this.headers[5].title = this.$t("vehicle_entry.datatable.version");
      this.headers[6].title = this.$t("vehicle_entry.datatable.year");
    },
    open(location) {
      this.location = location;
      this.dialog = true;
      this.getCars();
    },
    close() {
      this.dialog = false;
      this.location = null;
      this.cars = [];
      this.total = 0;
    },
    getCars() {
      this.loading = true;
      const sort = this.pagination.sortBy?.[0] || {
        key: "_id",
        order: "desc",
      };
      getRequest(
        "/car/location",
        `id=${this.location}&page=${this.pagination.page}&limit=${this.pagination.itemsPerPage}&sort_by=${sort.key}&descending=${sort.order === "desc"}`,
      )
        .then((response) => {
          this.cars = response.cars;
          this.total = response.count;
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },
    openQrDialog(data) {
      this.$emit("qr", data);
    },
    showImages(images) {
      this.$emit("images", { type: "string", images });
    },
    formatMonthYear(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const month = String(date.getUTCMonth() + 1).padStart(2, "0"); // months are 0-indexed
      const year = date.getUTCFullYear();
      return `${month}/${year}`;
    },
    updatePagination(options) {
      this.pagination = {
        ...options,
        itemsPerPage: options.itemsPerPage ? options.itemsPerPage : 5,
        groupBy:
          options.groupBy && options.groupBy.length ? options.groupBy : ["_id"],
        sortBy:
          options.sortBy && options.sortBy.length
            ? options.sortBy
            : { key: "_id", order: "desc" },
        sortDesc:
          options.sortDesc && options.sortDesc.length
            ? options.sortDesc
            : [true],
      };
      this.activeRow = null;
    },
  },
};
</script>

<style scoped>
@media (max-width: 959.98px) {
  .cars-list-table-host :deep(.v-table__wrapper),
  .cars-list-table-host :deep(.v-data-table__wrapper),
  .cars-list-table-host :deep(.v-data-table__wrapper__scroll) {
    display: none !important;
  }
  .cars-list-table-host :deep(.v-data-table-footer) {
    padding-inline: 12px;
  }
}
@media (min-width: 960px) {
  .cars-list-mobile-list {
    display: none !important;
  }
}

.cars-list-mobile-card {
  position: relative;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}
.cars-list-mobile-card--selected {
  outline: 2px solid rgb(var(--v-theme-primary));
}
.cars-list-mobile-card__main {
  display: flex;
  align-items: flex-start;
  padding: 10px;
  gap: 10px;
}
.cars-list-mobile-card__media {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}
.cars-list-mobile-card__qr-value-btn {
  min-width: 72px;
  padding: 4px 8px;
  border: 1px solid #bdbdbd;
  border-radius: 4px;
  background: #fff;
  font-size: 0.8rem;
  font-weight: 700;
  line-height: 1.2;
  color: #424242;
  cursor: pointer;
  text-align: center;
}
.cars-list-mobile-card__info {
  flex: 1;
  min-width: 0;
}
.cars-list-mobile-card__image-btn {
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}
.cars-list-mobile-card__img {
  width: 92px;
  height: 92px;
  object-fit: cover;
  border-radius: 6px;
  display: block;
  background: #212121;
}
</style>
